import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Palette, RotateCcw,
  ArrowLeft, Check, Loader2, Share2, Globe, Volume2, VolumeX,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { storiesApi } from '../api/client';
import { resolveMediaUrl } from '../lib/mediaUrl';
import { loadFontsFromStory } from '../lib/googleFonts';
import ShapeLayerRenderer from '../components/canvas/renderers/ShapeLayerRenderer';

// ── Animation keyframes (injected once into the document) ────────
const PLAYER_KEYFRAMES = `
@keyframes fade-in      { from { opacity: 0; } }
@keyframes fly-in-left  { from { transform: translateX(-120%); } }
@keyframes fly-in-right { from { transform: translateX(120%); } }
@keyframes fly-in-top   { from { transform: translateY(-120%); } }
@keyframes fly-in-bottom{ from { transform: translateY(120%); } }
@keyframes zoom-in      { from { transform: scale(0.5); opacity: 0; } }
@keyframes rotate-in-left  { from { transform: rotate(-90deg); opacity: 0; } }
@keyframes rotate-in-right { from { transform: rotate(90deg);  opacity: 0; } }
@keyframes drop         { from { transform: translateY(-60px); opacity: 0; } }
@keyframes pulse        { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
`;
if (typeof document !== 'undefined' && !document.getElementById('story-player-keyframes')) {
  const s = document.createElement('style');
  s.id = 'story-player-keyframes';
  s.textContent = PLAYER_KEYFRAMES;
  document.head.appendChild(s);
}

// ── Color utilities ──────────────────────────────────────────────
function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return [0, 0, 0];
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ];
}

function relativeLuminance(r, g, b) {
  return [r, g, b].reduce((acc, c, i) => {
    const s = c / 255;
    const lin = s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    return acc + lin * [0.2126, 0.7152, 0.0722][i];
  }, 0);
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (t) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(hue2rgb(h + 1 / 3))}${toHex(hue2rgb(h))}${toHex(hue2rgb(h - 1 / 3))}`;
}

/**
 * Derive a beautiful contrasting outer surround from the page background.
 * Dark page → vibrant pastel gradient. Light page → deep rich dark gradient.
 */
function getPlayerSurroundBg(bg) {
  const DARK_FALLBACK = 'linear-gradient(135deg, #f8d7ea, #e8d5f8, #d5e8f8)';
  const LIGHT_FALLBACK = 'linear-gradient(135deg, #1a0533, #0d1f3c)';

  function surroundFromRgb(r, g, b) {
    const lum = relativeLuminance(r, g, b);
    const [h, s, l] = rgbToHsl(r, g, b);
    const isDark = lum < 0.35 || l < 45;

    if (isDark) {
      if (s < 12) return { background: DARK_FALLBACK };
      const h1 = (h + 40) % 360;
      const h2 = (h + 80) % 360;
      const sat = Math.min(Math.max(s + 10, 50), 70);
      return {
        background: `linear-gradient(135deg, ${hslToHex(h1, sat, 88)}, ${hslToHex(h2, sat - 8, 92)})`,
      };
    } else {
      if (s < 12) return { background: LIGHT_FALLBACK };
      const h1 = (h + 180) % 360;
      const h2 = (h + 200) % 360;
      const sat = Math.min(s + 25, 80);
      return {
        background: `linear-gradient(135deg, ${hslToHex(h1, sat, 14)}, ${hslToHex(h2, sat - 10, 20)})`,
      };
    }
  }

  if (!bg) return { background: DARK_FALLBACK };

  if (bg.type === 'gradient' && bg.gradient?.stops.length) {
    const { stops } = bg.gradient;
    const [ar, ag, ab] = stops.reduce(
      ([sr, sg, sb], s) => {
        const [r, g, b] = hexToRgb(s.color);
        return [sr + r, sg + g, sb + b];
      },
      [0, 0, 0]
    ).map((v) => v / stops.length);
    return surroundFromRgb(ar, ag, ab);
  }

  if (bg.type === 'color' && bg.color) {
    const [r, g, b] = hexToRgb(bg.color);
    return surroundFromRgb(r, g, b);
  }

  return { background: 'linear-gradient(135deg, #e8eaf6, #fce4ec)' };
}

// ── Background CSS helper ────────────────────────────────────────
function bgToCss(bg) {
  if (!bg) return { background: '#000' };

  if (bg.type === 'color') {
    if (bg.overlay) {
      const overlay = `rgba(0,0,0,${bg.overlay.opacity ?? 0.3})`;
      return { background: `linear-gradient(${overlay}, ${overlay}), ${bg.color || '#000'}` };
    }
    return { background: bg.color || '#000' };
  }

  if (bg.type === 'gradient' && bg.gradient) {
    const { type, direction, stops } = bg.gradient;
    const stopsStr = stops
      .sort((a, b) => a.position - b.position)
      .map((s) => `${s.color} ${s.position}%`)
      .join(', ');
    const grad =
      type === 'linear'
        ? `linear-gradient(${direction}deg, ${stopsStr})`
        : `radial-gradient(circle, ${stopsStr})`;
    return { background: grad };
  }

  if (bg.type === 'image' && bg.mediaSrc) {
    const overlay = bg.overlay
      ? `linear-gradient(rgba(0,0,0,${bg.overlay.opacity}), rgba(0,0,0,${bg.overlay.opacity}))`
      : null;
    const resolvedSrc = resolveMediaUrl(bg.mediaSrc);
    return {
      backgroundImage: overlay
        ? `${overlay}, url(${resolvedSrc})`
        : `url(${resolvedSrc})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }

  return { background: '#000' };
}

// Reference canvas width used in the editor
const EDITOR_CANVAS_W = 412;

function hexToRgba(hex, opacity) {
  if (!hex?.startsWith('#')) return hex || 'transparent';
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
}

function getAnimationStyle(animation) {
  if (!animation?.preset || animation.preset === 'none') return {};
  return {
    animationName: animation.preset,
    animationDuration: `${animation.duration ?? 500}ms`,
    animationDelay: `${animation.delay ?? 0}ms`,
    animationTimingFunction: animation.easing || 'ease',
    animationFillMode: 'both',
    animationIterationCount: animation.preset === 'pulse' ? 'infinite' : 1,
  };
}

// ── Layer rendering ──────────────────────────────────────────────
function renderLayer(layer, pageW, pageH, scale) {
  if (layer.hidden) return null;
  const { x, y, width, height, rotation, opacity, zIndex } = layer.transform;

  // Outer: positions the layer, no overflow clip (allows fly-in from outside bounds)
  const posStyle = {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    width: `${width}%`,
    height: `${height}%`,
    zIndex,
    overflow: 'visible',
  };

  // Inner: rotation, opacity, animation
  const wrapStyle = {
    width: '100%',
    height: '100%',
    opacity,
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    overflow: 'hidden',
    ...getAnimationStyle(layer.animation),
  };

  const d = layer.data;

  if (d.type === 'text') {
    const s = d.style ?? {};
    const padding = s.padding || [0, 0, 0, 0];
    const bgStyle = s.backgroundColor
      ? {
          background: hexToRgba(s.backgroundColor, s.backgroundOpacity ?? 1),
          padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
          borderRadius: (s.borderRadius ?? 0) * scale,
          width: '100%',
        }
      : {
          padding: `${padding[0] * scale}px ${padding[1] * scale}px ${padding[2] * scale}px ${padding[3] * scale}px`,
          width: '100%',
        };
    const displayContent = s.textTransform === 'capitalize'
      ? d.content?.toLowerCase()
      : d.content;
    return (
      <div key={layer.id} style={posStyle}>
        <div style={{ ...wrapStyle, overflow: 'visible', display: 'flex', alignItems: 'center' }}>
          <div style={bgStyle}>
            <p style={{
              fontFamily: `'${s.fontFamily || 'sans-serif'}', sans-serif`,
              fontSize: (s.fontSize || 16) * scale,
              fontWeight: s.fontWeight || '400',
              lineHeight: s.lineHeight || 1.4,
              letterSpacing: (s.letterSpacing || 0) * scale,
              textAlign: s.textAlign || 'left',
              textTransform: s.textTransform || 'none',
              color: s.color || '#fff',
              textShadow: s.textShadow ?? undefined,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              margin: 0, padding: 0, width: '100%',
            }}>
              {displayContent}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (d.type === 'shape') {
    return (
      <div key={layer.id} style={posStyle}>
        <div style={wrapStyle}>
          <ShapeLayerRenderer layer={layer} />
        </div>
      </div>
    );
  }

  if (d.type === 'image') {
    const focal = d.focalPoint;
    const objPos = focal ? `${focal.x * 100}% ${focal.y * 100}%` : 'center';
    return (
      <div key={layer.id} style={posStyle}>
        <div style={wrapStyle}>
          <img src={resolveMediaUrl(d.src)} alt={d.alt || ''}
               style={{ width: '100%', height: '100%', objectFit: d.objectFit || 'cover', objectPosition: objPos, display: 'block' }} />
        </div>
      </div>
    );
  }

  if (d.type === 'video') {
    return (
      <div key={layer.id} style={posStyle}>
        <div style={wrapStyle}>
          <video
            src={resolveMediaUrl(d.src)}
            autoPlay loop muted playsInline
            style={{ width: '100%', height: '100%', objectFit: d.objectFit || 'cover', display: 'block' }}
          />
        </div>
      </div>
    );
  }

  if (d.type === 'overlay') {
    return (
      <div key={layer.id} style={posStyle}>
        <div style={{ ...wrapStyle, background: d.fill }} />
      </div>
    );
  }

  if (d.type === 'form') {
    return <FormPlayerLayer key={layer.id} layer={layer} style={{ ...posStyle, opacity, overflow: 'hidden' }} scale={scale} />;
  }

  if (d.type === 'interactive') {
    return <InteractivePlayerLayer key={layer.id} layer={layer} style={{ ...posStyle, opacity, overflow: 'hidden' }} scale={scale} />;
  }

  if (d.type === 'icon') {
    return (
      <div key={layer.id} style={posStyle}>
        <div style={{ ...wrapStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: d.color, fontSize: d.fontSize * scale, lineHeight: 1 }}>
          <i className={d.iconClass} />
        </div>
      </div>
    );
  }

  if (d.type === 'cta') {
    const s = d.style || {};
    const pad = s.padding || [12, 24, 12, 24];
    return (
      <div key={layer.id} style={posStyle}>
        <div style={{ ...wrapStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <a
            href={d.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: s.backgroundColor,
              color: s.color,
              fontSize: (s.fontSize || 14) * scale,
              fontWeight: s.fontWeight,
              borderRadius: (s.borderRadius || 0) * scale,
              padding: `${pad[0] * scale}px ${pad[1] * scale}px ${pad[2] * scale}px ${pad[3] * scale}px`,
              border: s.border || 'none',
              display: 'inline-block',
              fontFamily: 'Inter, sans-serif',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textDecoration: 'none',
              cursor: 'pointer',
            }}
          >
            {d.text || 'CTA Button'}
          </a>
        </div>
      </div>
    );
  }

  return null;
}

// ── Interactive form layer for the player ────────────────────────
function FormPlayerLayer({ layer, style, scale }) {
  const data = layer.data;
  const s = data.style ?? {};
  const fields = data.fields || [];

  const bgOpacity   = (s.backgroundOpacity ?? 90) / 100;
  const bg          = hexToRgba(s.backgroundColor || '#0a0a19', bgOpacity);
  const textColor   = s.textColor       || '#ffffff';
  const accentColor = s.accentColor     || 'var(--brand-primary)';
  const buttonColor = s.buttonColor     || 'var(--brand-primary)';
  const borderRadius = s.borderRadius   ?? 12;

  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');

  function set(name, value) {
    setValues(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    const url = data.actionXhrUrl || data.actionUrl;
    if (!url) {
      setStatus('error');
      setMessage('No submission URL configured.');
      return;
    }
    setStatus('submitting');
    // Build key-value payload from field names
    const payload = {};
    fields.forEach(f => {
      if (f.type === 'hidden') {
        payload[f.name] = f.defaultValue || '';
      } else if (f.type !== 'submit') {
        payload[f.name] = values[f.name] ?? '';
      }
    });
    try {
      await api.post(url, payload);
      setStatus('success');
      setMessage(data.successMessage || 'Thank you!');
      setValues({});
    } catch {
      setStatus('error');
      setMessage(data.errorMessage || 'Something went wrong. Please try again.');
    }
  }

  const sc = scale || 1;

  const inputStyle = {
    width: '100%',
    padding: `${7 * sc}px ${10 * sc}px`,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 7 * sc,
    color: textColor,
    fontSize: 12 * sc,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 10 * sc,
    color: 'rgba(255,255,255,0.55)',
    display: 'block',
    marginBottom: 3 * sc,
    fontWeight: 600,
    letterSpacing: 0.3,
  };

  const reqMark = <span style={{ color: accentColor }}> *</span>;

  function renderField(field) {
    if (field.type === 'hidden') return null;

    if (field.type === 'submit') {
      return (
        <button
          key={field.id}
          type="submit"
          disabled={status === 'submitting'}
          style={{
            width: '100%',
            padding: `${9 * sc}px ${12 * sc}px`,
            background: status === 'submitting' ? 'rgba(99,102,241,0.5)' : buttonColor,
            color: '#fff',
            border: 'none',
            borderRadius: 50 * sc,
            fontSize: 13 * sc,
            fontWeight: 700,
            cursor: status === 'submitting' ? 'default' : 'pointer',
            letterSpacing: 0.5,
            marginTop: 2 * sc,
          }}
        >
          {status === 'submitting' ? 'Sending…' : (field.label || 'Submit')}
        </button>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && reqMark}</label>}
          <textarea
            name={field.name}
            value={values[field.name] || ''}
            onChange={e => set(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            style={{ ...inputStyle, resize: 'none', height: 60 * sc }}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && reqMark}</label>}
          <select
            name={field.name}
            value={values[field.name] || ''}
            onChange={e => set(field.name, e.target.value)}
            required={field.required}
            style={{ ...inputStyle, appearance: 'none' }}
          >
            <option value="" style={{ background: '#1f2937' }}>{field.placeholder || 'Select…'}</option>
            {(field.options || []).map(o => (
              <option key={o.value} value={o.value} style={{ background: '#1f2937' }}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8 * sc, cursor: 'pointer' }}>
          <input
            type="checkbox"
            name={field.name}
            checked={!!values[field.name]}
            onChange={e => set(field.name, e.target.checked)}
            style={{ accentColor, width: 14 * sc, height: 14 * sc, flexShrink: 0 }}
          />
          <span style={{ fontSize: 12 * sc, color: textColor }}>{field.label}</span>
        </label>
      );
    }

    if (field.type === 'radio') {
      const options = field.options?.length ? field.options : [];
      return (
        <div key={field.id}>
          {field.label && <label style={labelStyle}>{field.label}{field.required && reqMark}</label>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 * sc }}>
            {options.map(o => (
              <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 7 * sc, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={field.name}
                  value={o.value}
                  checked={values[field.name] === o.value}
                  onChange={e => set(field.name, e.target.value)}
                  style={{ accentColor, width: 13 * sc, height: 13 * sc, flexShrink: 0 }}
                />
                <span style={{ fontSize: 12 * sc, color: textColor }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    const htmlType = field.type === 'phone' ? 'tel' : field.type;
    return (
      <div key={field.id}>
        {field.label && <label style={labelStyle}>{field.label}{field.required && reqMark}</label>}
        <input
          type={htmlType}
          name={field.name}
          value={values[field.name] || ''}
          onChange={e => set(field.name, e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          style={inputStyle}
        />
      </div>
    );
  }

  const containerStyle = {
    width: '100%',
    height: '100%',
    background: bg,
    borderRadius,
    padding: `${14 * sc}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: 9 * sc,
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(16px)',
    overflow: 'auto',
    boxSizing: 'border-box',
  };

  const headerBadge = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 * sc }}>
      <span style={{
        background: accentColor, color: '#fff',
        fontSize: 8 * sc, fontWeight: 700,
        padding: `${2 * sc}px ${7 * sc}px`,
        borderRadius: 4 * sc, textTransform: 'uppercase', letterSpacing: 1.2,
      }}>FORM</span>
      {data.formTitle && (
        <span style={{ fontSize: 12 * sc, fontWeight: 700, color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.formTitle}
        </span>
      )}
    </div>
  );

  return (
    <div
      style={{ ...style, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}
    >
      {status === 'success' ? (
        <div style={{ ...containerStyle, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 32 * sc, textAlign: 'center' }}>✅</div>
          <p style={{ color: textColor, fontSize: 14 * sc, fontWeight: 700, textAlign: 'center', margin: 0 }}>
            {message}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={containerStyle}>
          {headerBadge}
          {status === 'error' && (
            <div style={{
              padding: `${6 * sc}px ${10 * sc}px`,
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.4)',
              borderRadius: 6 * sc,
              fontSize: 11 * sc,
              color: '#fca5a5',
            }}>
              {message}
            </div>
          )}
          {fields.filter(f => f.type !== 'hidden').map(renderField)}
        </form>
      )}
    </div>
  );
}

// ── Interactive layer for the player ────────────────────────────
const INTERACTIVE_META = {
  'binary-poll': { label: 'Poll',    emoji: '📊', color: 'var(--brand-400)' },
  'text-poll':   { label: 'Poll',    emoji: '📊', color: 'var(--brand-400)' },
  'image-poll':  { label: 'Poll',    emoji: '🖼️', color: '#a78bfa' },
  'text-quiz':   { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
  'image-quiz':  { label: 'Quiz',    emoji: '🧠', color: '#f472b6' },
  'slider':      { label: 'Slider',  emoji: '🎚️', color: 'var(--brand-400)' },
  'results':     { label: 'Results', emoji: '📈', color: '#34d399' },
};

function InteractivePlayerLayer({ layer, style, scale }) {
  const data = layer.data;
  const isDark = (data.theme || 'dark') === 'dark';
  const [selected, setSelected] = useState(null);
  const [sliderVal, setSliderVal] = useState(data.sliderInitialValue ?? 50);
  const [submitted, setSubmitted] = useState(false);

  const sc = scale || 1;
  const meta = INTERACTIVE_META[data.interactiveType] || { label: 'Interactive', emoji: '✨', color: 'var(--brand-400)' };

  const bg        = isDark ? 'rgba(8,8,20,0.92)'          : 'rgba(255,255,255,0.96)';
  const textColor = isDark ? '#ffffff'                     : '#111827';
  const muted     = isDark ? 'rgba(255,255,255,0.60)'      : 'rgba(0,0,0,0.50)';
  const optBg     = isDark ? 'rgba(255,255,255,0.10)'      : 'rgba(0,0,0,0.06)';
  const optBorder = isDark ? 'rgba(255,255,255,0.22)'      : 'rgba(0,0,0,0.14)';

  function handleSelect(id) {
    if (submitted) return;
    setSelected(id);
    const isQuiz = data.interactiveType?.includes('quiz');
    if (!isQuiz) setSubmitted(true);
  }

  function renderBinaryPoll() {
    const opts = data.options || [];
    const accents = ['var(--brand-primary)', '#ec4899'];
    const icons = ['👍', '👎'];
    if (submitted) {
      const chosen = opts.find(o => o.id === selected);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 * sc }}>
          <span style={{ fontSize: 28 * sc }}>🎉</span>
          <p style={{ margin: 0, fontSize: 13 * sc, color: textColor, fontWeight: 600, textAlign: 'center' }}>
            You voted: {chosen?.text || ''}
          </p>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', gap: 8 * sc }}>
        {[0, 1].map(i => {
          const opt = opts[i] || {};
          const isSelected = selected === (opt.id || String(i));
          return (
            <button key={i} onClick={() => handleSelect(opt.id || String(i))} style={{
              flex: 1, borderRadius: 12 * sc, padding: `${13 * sc}px ${8 * sc}px`,
              background: isSelected ? accents[i] : `${accents[i]}33`,
              border: `2px solid ${accents[i]}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 * sc,
              cursor: 'pointer', transition: 'background 0.15s',
            }}>
              <span style={{ fontSize: 22 * sc, lineHeight: 1 }}>{icons[i]}</span>
              <span style={{ fontSize: 13 * sc, fontWeight: 700, color: '#ffffff', textAlign: 'center' }}>
                {opt.text || ['Yes', 'No'][i]}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  function renderOptions() {
    const opts = (data.options || []).slice(0, 4);
    const isQuiz = data.interactiveType?.includes('quiz');
    const letters = ['A', 'B', 'C', 'D'];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 * sc }}>
        {opts.map((o, i) => {
          const isSelected = selected === o.id;
          const correct = 'isCorrect' in o ? o.isCorrect : false;
          const showCorrect = submitted && correct;
          const showWrong   = submitted && isSelected && !correct;
          let optBackground = isSelected ? `${meta.color}25` : optBg;
          let optBorderColor = isSelected ? meta.color : optBorder;
          if (showCorrect) { optBackground = 'rgba(34,197,94,0.20)';  optBorderColor = '#4ade80'; }
          if (showWrong)   { optBackground = 'rgba(239,68,68,0.15)';  optBorderColor = '#f87171'; }
          return (
            <button key={o.id || i} onClick={() => handleSelect(o.id)} style={{
              background: optBackground, border: `1.5px solid ${optBorderColor}`,
              borderRadius: 10 * sc, padding: `${9 * sc}px ${12 * sc}px`,
              display: 'flex', alignItems: 'center', gap: 9 * sc,
              cursor: submitted ? 'default' : 'pointer', width: '100%', textAlign: 'left',
              transition: 'all 0.15s',
            }}>
              {isQuiz && (
                <span style={{
                  width: 24 * sc, height: 24 * sc, borderRadius: 6 * sc, flexShrink: 0,
                  background: showCorrect ? '#22c55e' : showWrong ? '#ef4444' : isSelected ? meta.color : 'rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11 * sc, fontWeight: 800, color: '#fff',
                }}>
                  {letters[i]}
                </span>
              )}
              <span style={{ flex: 1, fontSize: 13 * sc, fontWeight: 500, color: textColor, lineHeight: 1.35 }}>
                {o.text || `Option ${i + 1}`}
              </span>
              {showCorrect && <span style={{ fontSize: 14 * sc, color: '#4ade80' }}>✓</span>}
              {showWrong   && <span style={{ fontSize: 14 * sc, color: '#f87171' }}>✗</span>}
            </button>
          );
        })}
        {isQuiz && !submitted && selected && (
          <button onClick={() => setSubmitted(true)} style={{
            marginTop: 4 * sc, padding: `${9 * sc}px`,
            background: meta.color, color: '#fff', border: 'none',
            borderRadius: 50 * sc, fontSize: 13 * sc, fontWeight: 700, cursor: 'pointer',
          }}>
            Submit Answer
          </button>
        )}
      </div>
    );
  }

  function renderSlider() {
    const min = data.sliderMin ?? 0;
    const max = data.sliderMax ?? 100;
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 * sc }}>
          <span style={{ background: meta.color, color: '#fff', fontSize: 18 * sc, fontWeight: 800, borderRadius: 8 * sc, padding: `${3 * sc}px ${14 * sc}px` }}>
            {sliderVal}
          </span>
        </div>
        <div style={{ padding: `0 ${12 * sc}px` }}>
          <input
            type="range"
            min={min}
            max={max}
            step={data.sliderStep ?? 1}
            value={sliderVal}
            onChange={e => setSliderVal(Number(e.target.value))}
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', accentColor: meta.color, cursor: 'pointer' }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: `0 ${6 * sc}px`, fontSize: 11 * sc, color: muted, marginTop: 4 * sc }}>
          <span>{min}</span><span>{max}</span>
        </div>
      </div>
    );
  }

  function renderResults() {
    const cats = (data.resultsCategories || []).slice().sort((a, b) => b.threshold - a.threshold);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 * sc }}>
        {cats.map((cat, i) => (
          <div key={cat.id || i} style={{
            background: optBg, border: `1.5px solid ${optBorder}`,
            borderRadius: 10 * sc, padding: `${10 * sc}px ${12 * sc}px`,
            display: 'flex', alignItems: 'center', gap: 10 * sc,
          }}>
            <div style={{ width: 8 * sc, height: 8 * sc, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <p style={{ margin: 0, fontSize: 13 * sc, fontWeight: 700, color: textColor, lineHeight: 1.3 }}>{cat.title}</p>
              {cat.description && (
                <p style={{ margin: `${2 * sc}px 0 0`, fontSize: 11 * sc, color: muted, lineHeight: 1.3 }}>{cat.description}</p>
              )}
            </div>
            <span style={{ fontSize: 10 * sc, color: meta.color, fontWeight: 700, flexShrink: 0 }}>≥{cat.threshold}%</span>
          </div>
        ))}
      </div>
    );
  }

  function renderContent() {
    if (data.interactiveType === 'binary-poll') return renderBinaryPoll();
    if (data.interactiveType === 'slider')      return renderSlider();
    if (data.interactiveType === 'results')     return renderResults();
    return renderOptions();
  }

  return (
    <div style={{ ...style, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
      <div style={{
        width: '100%', height: '100%', background: bg,
        borderRadius: 16 * sc, padding: 13 * sc,
        display: 'flex', flexDirection: 'column', gap: 10 * sc,
        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.10)'}`,
        backdropFilter: 'blur(20px)', overflow: 'auto', boxSizing: 'border-box',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 * sc }}>
          <span style={{ fontSize: 15 * sc, lineHeight: 1 }}>{meta.emoji}</span>
          <span style={{
            background: meta.color, color: '#fff',
            fontSize: 10 * sc, fontWeight: 700, letterSpacing: '0.07em',
            textTransform: 'uppercase', padding: `${2 * sc}px ${8 * sc}px`, borderRadius: 20 * sc,
          }}>
            {meta.label}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 15 * sc, fontWeight: 700, color: textColor, lineHeight: 1.4, letterSpacing: '-0.01em' }}>
          {data.prompt || ''}
        </p>
        {renderContent()}
        {submitted && !['results', 'slider'].includes(data.interactiveType) && (
          <p style={{ margin: 0, fontSize: 10 * sc, color: muted, textAlign: 'center' }}>Thanks for responding!</p>
        )}
      </div>
    </div>
  );
}

// ── Gradient Stop Editor ─────────────────────────────────────────
function GradientEditor({ background, onChange, onSave, onClose, saving }) {
  const grad = background.gradient;

  function updateStop(index, field, value) {
    const stops = grad.stops.map((s, i) =>
      i === index ? { ...s, [field]: value } : s
    );
    onChange({ ...background, gradient: { ...grad, stops } });
  }

  function updateDirection(dir) {
    onChange({ ...background, gradient: { ...grad, direction: dir } });
  }

  const presetDirections = [
    { label: '↓', deg: 180 }, { label: '↗', deg: 45 }, { label: '→', deg: 90 },
    { label: '↘', deg: 135 }, { label: '↑', deg: 0 }, { label: '↙', deg: 225 },
    { label: '←', deg: 270 }, { label: '↖', deg: 315 },
  ];

  const presetGradients = [
    { stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 100 }] },
    { stops: [{ color: '#f093fb', position: 0 }, { color: '#f5576c', position: 100 }] },
    { stops: [{ color: '#4facfe', position: 0 }, { color: '#00f2fe', position: 100 }] },
    { stops: [{ color: '#43e97b', position: 0 }, { color: '#38f9d7', position: 100 }] },
    { stops: [{ color: '#fa709a', position: 0 }, { color: '#fee140', position: 100 }] },
    { stops: [{ color: '#a18cd1', position: 0 }, { color: '#fbc2eb', position: 100 }] },
    { stops: [{ color: '#ffecd2', position: 0 }, { color: '#fcb69f', position: 100 }] },
    { stops: [{ color: '#0f0f0f', position: 0 }, { color: '#1a1a2e', position: 100 }] },
    { stops: [{ color: '#ff6b6b', position: 0 }, { color: '#feca57', position: 100 }] },
    { stops: [{ color: '#2d3436', position: 0 }, { color: '#636e72', position: 100 }] },
    { stops: [{ color: '#6a11cb', position: 0 }, { color: '#2575fc', position: 100 }] },
    { stops: [{ color: '#e96443', position: 0 }, { color: '#904e95', position: 100 }] },
  ];

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 rounded-2xl shadow-2xl"
      style={{
        background: 'rgba(15,15,15,0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.12)',
        width: 320,
        padding: '16px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Palette size={14} className="text-indigo-400" />
          <span className="text-sm font-semibold text-white">Gradient Editor</span>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors">
          <X size={15} />
        </button>
      </div>

      {/* Preview strip */}
      <div className="h-8 rounded-lg mb-3 w-full" style={{
        background: (() => {
          const stops = grad.stops.sort((a, b) => a.position - b.position).map((s) => `${s.color} ${s.position}%`).join(', ');
          return `linear-gradient(${grad.direction}deg, ${stops})`;
        })(),
      }} />

      {/* Color stops */}
      <div className="space-y-2 mb-3">
        {grad.stops.map((stop, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="relative flex-shrink-0">
              <div className="w-8 h-8 rounded-lg border-2 border-white/20 cursor-pointer overflow-hidden"
                   style={{ background: stop.color }}>
                <input
                  type="color"
                  value={stop.color}
                  onChange={(e) => updateStop(i, 'color', e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                />
              </div>
            </div>
            <div className="flex-1">
              <input
                type="range"
                min={0}
                max={100}
                value={stop.position}
                onChange={(e) => updateStop(i, 'position', parseInt(e.target.value))}
                className="w-full accent-indigo-500"
              />
            </div>
            <span className="text-xs text-white/50 w-8 text-right">{stop.position}%</span>
          </div>
        ))}
      </div>

      {/* Direction */}
      <div className="mb-3">
        <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Direction</p>
        <div className="flex gap-1 flex-wrap">
          {presetDirections.map(({ label, deg }) => (
            <button
              key={deg}
              onClick={() => updateDirection(deg)}
              className="w-8 h-8 rounded-md text-sm transition-all"
              style={{
                background: grad.direction === deg ? 'var(--brand-primary)' : 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {label}
            </button>
          ))}
          <div className="flex items-center gap-1 ml-1">
            <input
              type="number"
              min={0}
              max={360}
              value={grad.direction}
              onChange={(e) => updateDirection(parseInt(e.target.value) || 0)}
              className="w-12 text-xs text-center rounded-md px-1 py-1"
              style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }}
            />
            <span className="text-xs text-white/40">°</span>
          </div>
        </div>
      </div>

      {/* Preset gradients */}
      <div className="mb-4">
        <p className="text-xs text-white/50 mb-2 uppercase tracking-wide">Presets</p>
        <div className="grid grid-cols-6 gap-1.5">
          {presetGradients.map((preset, i) => {
            const stops = preset.stops.map((s) => `${s.color} ${s.position}%`).join(', ');
            return (
              <button
                key={i}
                onClick={() => onChange({ ...background, gradient: { ...grad, stops: preset.stops } })}
                className="h-7 rounded-md transition-transform hover:scale-110"
                style={{
                  background: `linear-gradient(${grad.direction}deg, ${stops})`,
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onChange({
            ...background,
            gradient: { ...grad, stops: [{ color: '#667eea', position: 0 }, { color: '#764ba2', position: 100 }] }
          })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors"
          style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
          style={{ background: saving ? 'rgba(99,102,241,0.5)' : 'var(--brand-primary)', color: '#fff' }}
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
          {saving ? 'Saving…' : 'Apply & Save'}
        </button>
      </div>
    </div>
  );
}

// ── Main StoryPlayer Component ───────────────────────────────────
export default function StoryPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showGradientEditor, setShowGradientEditor] = useState(false);
  const [localBackground, setLocalBackground] = useState(null);
  const [savingBg, setSavingBg] = useState(false);
  const [progressing, setProgressing] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [canvasWidth, setCanvasWidth] = useState(EDITOR_CANVAS_W);

  const containerRef = useRef(null);
  const autoTimer = useRef(null);
  const audioRef = useRef(null);
  const [audioMuted, setAudioMuted] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Track actual canvas width for pixel-perfect scaling at any resolution
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setCanvasWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  // Background audio — start muted (browser autoplay policy), user taps to unmute
  // API returns `storyMetadata`; editor store normalises it to `metadata` — support both
  const storyMeta = story?.storyMetadata ?? story?.metadata ?? {};

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !storyMeta.backgroundAudioSrc) return;
    audio.src = storyMeta.backgroundAudioSrc;
    audio.loop = storyMeta.backgroundAudioLoop !== false;
    audio.volume = 1;
    audio.muted = false;
    audio.play().catch(() => {
      // Browser blocked autoplay — fall back to muted so the icon reflects reality
      audio.muted = true;
      setAudioMuted(true);
    });
  }, [storyMeta.backgroundAudioSrc, storyMeta.backgroundAudioLoop]);

  function toggleAudioMute() {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !audioMuted;
    audio.muted = next;
    if (!next) audio.play().catch(() => {});
    setAudioMuted(next);
  }

  useEffect(() => {
    if (!id) { navigate('/marketing/story-builder'); return; }
    storiesApi.get(id)
      .then((s) => {
        const normalized = {
          ...s,
          id: s.id || s._id,
          pages: (s.pages || []).map(p => ({
            ...p,
            id: p.id || p._id,
            layers: (p.layers || []).map(l => ({ ...l, id: l.id || l._id })),
          })),
        };
        loadFontsFromStory(normalized);
        setStory(normalized);
        setLoading(false);
      })
      .catch(() => {
        toast.error('Story not found');
        navigate('/marketing/story-builder');
      });
  }, [id, navigate]);

  const pages = story?.pages.slice().sort((a, b) => a.pageIndex - b.pageIndex) || [];
  const currentPage = pages[currentIndex];

  // Sync local background when page changes
  useEffect(() => {
    if (currentPage) {
      setLocalBackground(currentPage.background);
      setShowGradientEditor(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentPage?.id]);

  // Auto-advance — use page duration if set, otherwise default 5 s
  const AUTO_PLAY_MS = 5000;
  useEffect(() => {
    const duration = currentPage?.autoAdvanceDuration ?? AUTO_PLAY_MS;
    autoTimer.current = setTimeout(() => {
      if (currentIndex < pages.length - 1) setCurrentIndex((i) => i + 1);
    }, duration);
    return () => { if (autoTimer.current) clearTimeout(autoTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentPage]);

  // Keyboard navigation
  const goNext = useCallback(() => {
    if (currentIndex < pages.length - 1) {
      setProgressing(true);
      setTimeout(() => setProgressing(false), 300);
      setCurrentIndex((i) => i + 1);
    }
  }, [currentIndex, pages.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  }, [currentIndex]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'Escape') navigate('/marketing/story-builder');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev, navigate]);

  // Click zones for navigation
  function handleContainerClick(e) {
    if (showGradientEditor) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (x < rect.width * 0.35) goPrev();
    else if (x > rect.width * 0.65) goNext();
  }

  // Save gradient change
  async function saveBackground() {
    if (!story || !currentPage || !localBackground) return;
    setSavingBg(true);
    try {
      await storiesApi.updatePageBackground(story.id, currentPage.id, localBackground);
      setStory((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pages: prev.pages.map((p) =>
            p.id === currentPage.id ? { ...p, background: localBackground } : p
          ),
        };
      });
      toast.success('Background saved!');
      setShowGradientEditor(false);
    } catch {
      toast.error('Failed to save background');
    } finally {
      setSavingBg(false);
    }
  }

  async function handleShare() {
    const url = `${window.location.origin}/marketing/story-builder/${id}/play`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Player link copied!');
    } catch {
      toast.error('Could not copy URL');
    }
  }

  const isGradientPage = currentPage?.background?.type === 'gradient';

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center" style={{ background: '#111' }}>
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/60">Loading story…</p>
        </div>
      </div>
    );
  }

  if (!story || pages.length === 0) {
    return (
      <div className="w-screen h-screen flex items-center justify-center" style={{ background: '#111' }}>
        <p className="text-white/60">No pages found in this story.</p>
      </div>
    );
  }

  const activeBg = localBackground ?? currentPage.background;
  const bgStyle = bgToCss(activeBg);
  const surroundStyle = getPlayerSurroundBg(activeBg);
  const scale = canvasWidth / EDITOR_CANVAS_W;

  return (
    <div
      className="w-screen h-screen flex flex-col items-center justify-center"
      style={{ ...surroundStyle, transition: 'background 0.6s ease' }}
    >
      {/* ── Top bar ── */}
      <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3"
           style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)' }}>
        {/* Back */}
        <button
          onClick={() => navigate('/marketing/story-builder')}
          className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Dashboard</span>
        </button>

        {/* Story title */}
        <div className="flex flex-col items-center">
          <p className="text-white text-sm font-semibold truncate max-w-[200px]">
            {story.metadata?.title}
          </p>
          <p className="text-white/50 text-xs">{story.metadata?.publisherName}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {story.status === 'published' && (
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white transition-all"
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <Share2 size={12} /> Share
            </button>
          )}
          <button
            onClick={() => navigate(`/marketing/story-builder/${id}/edit`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white transition-all"
            style={{ background: 'rgba(99,102,241,0.7)', border: '1px solid rgba(99,102,241,0.5)' }}
          >
            Edit
          </button>
        </div>
      </div>

      {/* ── Progress bar ── */}
      <div className="absolute top-0 left-0 right-0 z-50 flex gap-1 px-3 pt-1.5">
        {pages.map((_, i) => (
          <div
            key={i}
            className="flex-1 h-0.5 rounded-full overflow-hidden cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.25)' }}
            onClick={() => setCurrentIndex(i)}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                background: '#fff',
                width: i < currentIndex ? '100%' : i === currentIndex ? '100%' : '0%',
                opacity: i === currentIndex ? 1 : i < currentIndex ? 0.8 : 0.3,
              }}
            />
          </div>
        ))}
      </div>

      {/* ── Story canvas ── */}
      <div
        ref={containerRef}
        className={`relative overflow-hidden select-none ${isMobile ? '' : 'rounded-xl'}`}
        style={isMobile ? {
          width: '100%',
          height: '100vh',
          cursor: showGradientEditor ? 'default' : 'pointer',
          ...bgStyle,
        } : {
          height: 'calc(100vh - 200px)',
          width: 'calc((100vh - 200px) * 9 / 16)',
          cursor: showGradientEditor ? 'default' : 'pointer',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          ...bgStyle,
        }}
        onClick={handleContainerClick}
      >
        {/* Layers */}
        {currentPage.layers
          .filter((l) => !l.hidden)
          .sort((a, b) => (a.transform.zIndex || 0) - (b.transform.zIndex || 0))
          .map((layer) => renderLayer(layer, 9, 16, scale))
        }

        {/* Left click zone hint */}
        {currentIndex > 0 && !showGradientEditor && (
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
            style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
          >
            <ChevronLeft size={20} className="text-white" />
          </button>
        )}
        {currentIndex < pages.length - 1 && !showGradientEditor && (
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
            style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
          >
            <ChevronRight size={20} className="text-white" />
          </button>
        )}

        {/* Page counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs text-white/70 font-medium"
             style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          {currentIndex + 1} / {pages.length}
        </div>

        {/* Background audio mute toggle — only shown when audio is configured */}
        {storyMeta.backgroundAudioSrc && (
          <button
            onClick={(e) => { e.stopPropagation(); toggleAudioMute(); }}
            className="absolute bottom-4 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
            style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)', zIndex: 60 }}
            title={audioMuted ? 'Unmute background audio' : 'Mute background audio'}
          >
            {audioMuted
              ? <VolumeX size={16} className="text-white/80" />
              : <Volume2 size={16} className="text-white" />}
          </button>
        )}

        {/* Gradient editor floating button */}
        {/* {isGradientPage && !showGradientEditor && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowGradientEditor(true); }}
            className="absolute bottom-14 right-3 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, var(--brand-primary), #8b5cf6)',
              color: '#fff',
              boxShadow: '0 4px 16px rgba(99,102,241,0.5)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Palette size={13} />
            Edit Gradient
          </button>
        )} */}

        {/* Gradient Editor panel */}
        {isGradientPage && showGradientEditor && localBackground && (
          <GradientEditor
            background={localBackground}
            onChange={setLocalBackground}
            onSave={saveBackground}
            onClose={() => { setShowGradientEditor(false); setLocalBackground(currentPage.background); }}
            saving={savingBg}
          />
        )}
      </div>

      {/* ── Bottom controls ── */}
      <div className="absolute bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-6 py-3"
           style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}>
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
        >
          <ChevronLeft size={18} className="text-white" />
        </button>

        <div className="flex gap-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className="rounded-full transition-all"
              style={{
                width: i === currentIndex ? 20 : 6,
                height: 6,
                background: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={currentIndex === pages.length - 1}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-30"
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
        >
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>

      {/* Published indicator */}
      {story.status === 'published' && (
        <div className="absolute bottom-16 right-4 z-40 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
             style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)', color: '#34d399' }}>
          <Globe size={10} />
          Published
        </div>
      )}

      {/* Persistent background audio — mounted once, never remounts on page change */}
      <audio ref={audioRef} preload="auto" style={{ display: 'none' }} />
    </div>
  );
}
